import { Cell, CellAiConfigView } from "generated/graphql";
import { P } from "theme/typography";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { Box, Chip, Tooltip } from "@mui/material";
import { AutoAwesome, Key } from "@mui/icons-material";
import { useAppSelector } from "redux/hooks";
import { selectProfileBuilderAiMode } from "redux/slices/lensEditor";
import DescriptionTooltip from "components/FormField/DescriptionTooltip";

type CellPreviewProps = {
  cell: Cell;
  onClick?: () => void;
  cellAiConfig?: CellAiConfigView;
};

const CellPreview = ({ cell, onClick, cellAiConfig }: CellPreviewProps) => {
  const aiMode = useAppSelector(selectProfileBuilderAiMode);
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: "100%",
        cursor: onClick ? "pointer" : "default",
      }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}>
        {aiMode && cellAiConfig?.alternateName && (
          <Tooltip title="Alternate Name">
            <AutoAwesome color="primary" />
          </Tooltip>
        )}
        <P variant="h6">
          {aiMode && cellAiConfig?.alternateName ? cellAiConfig?.alternateName : cell.name}
          {!cell.optional && <>*</>}
        </P>
        <DescriptionTooltip description={cell.description} />
      </Box>
      {aiMode && cellAiConfig?.additionalPrompt && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}>
          <Tooltip title="Additional Prompt">
            <AutoAwesome sx={{ color: "primary.main" }} fontSize="small" />
          </Tooltip>
          <P variant="sm">{cellAiConfig?.additionalPrompt}</P>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          pr: 1,
        }}>
        <Chip label={cell.type} color="primary" size="small" />
        {cell.selectOptions?.length ? (
          <Chip
            label={
              cell.selectOptions.join(", ").slice(0, 50) + (cell.selectOptions.join(", ").length > 50 ? "..." : "")
            }
            size="small"
            icon={<FormatListBulletedIcon />}
            sx={{
              pl: 0.5,
            }}
          />
        ) : null}
        {cell.key && (
          <Chip
            label={cell.key}
            size="small"
            icon={<Key />}
            sx={{
              pl: 0.5,
            }}
          />
        )}
        {!aiMode && cellAiConfig?.alternateName && (
          <Chip
            color="primary"
            label={`AI Name: ${cellAiConfig?.alternateName}`}
            size="small"
            icon={<AutoAwesome />}
            sx={{
              pl: 0.5,
            }}
          />
        )}
        {!aiMode && cellAiConfig?.additionalPrompt && (
          <Chip
            color="primary"
            label="Additional prompt"
            size="small"
            icon={<AutoAwesome />}
            sx={{
              pl: 0.5,
            }}
          />
        )}
      </Box>
      {aiMode && cellAiConfig !== undefined && (
        <div className="rounded bg-gray-50 p-4 grid grid-cols-[minmax(0,max-content)_1fr] gap-2">
          {Object.entries(cellAiConfig).map((entry) =>
            entry[1] !== undefined && entry[1] !== null ? (
              <>
                <p className="text-xs font-bold">{entry[0]}</p>
                <p className="text-xs">{entry[1].toString()}</p>
              </>
            ) : (
              <></>
            )
          )}
        </div>
      )}
    </Box>
  );
};

export default CellPreview;
